/*
  KS Cart Goal calculations and rendering. Prestige owns cart mutations,
  product forms, and drawer section replacement.
*/


/**
 * @typedef {object} CartGoal
 * @property {number} index
 * @property {number} amount
 * @property {string} label
 */

/**
 * @typedef {object} CartState
 * @property {string | number | undefined} subtotal
 * @property {string | number | undefined} quantity
 * @property {string | undefined} currency
 * @property {CartLine[] | undefined} lines
 */

/**
 * @typedef {object} CartLine
 * @property {{
 *   product?: { id?: string | number },
 *   productId?: string | number
 * } | undefined} merchandise
 * @property {string | number | undefined} productId
 * @property {string | number | undefined} product_id
 * @property {{
 *   totalAmount?: { amount?: string | number }
 * } | undefined} cost
 */

/*
  Reads comma- or line-separated CURRENCY:AMOUNT pairs into a lookup.
*/
/**
 * @param {unknown} value
 * @returns {Map<string, number>}
 */
function parseAmounts(value) {
  /** @type {Map<string, number>} */
  const amounts = new Map();

  String(value)
    .split(/[,\n]+/)
    .forEach((entry) => {
      const separatorIndex = entry.indexOf(":");
      if (separatorIndex < 1) return;

      const currency = entry.slice(0, separatorIndex).trim().toUpperCase();
      const amount = Number(entry.slice(separatorIndex + 1).trim());

      if (
        /^[A-Z]{3}$/.test(currency) &&
        Number.isFinite(amount) &&
        amount > 0
      ) {
        amounts.set(currency, amount);
      }
    });

  return amounts;
}

/*
  Returns the enabled goals configured for the active currency, provided
  every amount exists and increases from one enabled goal to the next.
*/
/**
 * @param {HTMLElement} claim
 * @param {string} currency
 * @returns {CartGoal[]}
 */
function getGoals(element, currency) {
  /** @type {CartGoal[]} */
  const goals = [];

  for (let index = 1; index <= 3; index += 1) {
    if (element.dataset[`ksGoal${index}Enabled`] !== "true") continue;

    const amount = parseAmounts(element.dataset[`ksGoal${index}Amounts`]).get(
      currency,
    );
    if (amount === undefined || !Number.isFinite(amount)) return [];

    goals.push({
      index,
      amount,
      label: element.dataset[`ksGoal${index}Label`] || "",
    });
  }

  const amountsIncrease = goals.every((goal, index) => {
    const previousGoal = goals[index - 1];
    return previousGoal === undefined || goal.amount > previousGoal.amount;
  });

  return amountsIncrease ? goals : [];
}

/*
  Calculates progress across equal-width visual segments while respecting
  the actual monetary threshold assigned to each goal.
*/
/**
 * @param {number} subtotal
 * @param {CartGoal[]} goals
 * @returns {number}
 */
function getProgress(subtotal, goals) {
  const nextGoalIndex = goals.findIndex((goal) => subtotal < goal.amount);
  if (nextGoalIndex === -1) return 100;

  const nextGoal = goals[nextGoalIndex];
  if (!nextGoal) return 0;

  const previousAmount = goals[nextGoalIndex - 1]?.amount ?? 0;
  const segmentProgress =
    (subtotal - previousAmount) / (nextGoal.amount - previousAmount);

  return Math.min(
    100,
    Math.max(0, (nextGoalIndex + segmentProgress) * (100 / goals.length)),
  );
}

/*
  Formats a major-unit amount using the storefront language and currency.
*/
/**
 * @param {number} amount
 * @param {string} currency
 * @returns {string}
 */
function formatCurrency(amount, currency) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/*
  Normalizes numeric and GraphQL resource IDs for reliable comparisons.
*/
/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeResourceId(value) {
  return String(value || "").split("/").pop() || "";
}

/*
  Checks whether cart-line data identifies the configured gift product.
*/
/**
 * @param {HTMLElement} element
 * @param {CartState} cart
 * @returns {boolean | undefined}
 */
function getFreeGiftInCart(claim, cart) {
  const giftProductId = normalizeResourceId(claim.dataset.ksFreeGiftProductId);
  if (!giftProductId || !Array.isArray(cart.lines)) return undefined;

  const lineProductIds = cart.lines
    .map((line) => {
      const lineProductId =
        line.merchandise?.product?.id ??
        line.merchandise?.productId ??
        line.productId ??
        line.product_id;

      return normalizeResourceId(lineProductId);
    })
    .filter(Boolean);

  if (lineProductIds.length === 0) return undefined;

  return lineProductIds.includes(giftProductId);
}

/*
  Creates a short confetti burst when the free-gift claim becomes available.
*/
/**
 * @param {HTMLElement} claim
 */
function showFreeGiftConfetti(claim) {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  claim
    .querySelector(".ks-cart-goal-free-gift-confetti")
    ?.remove();

  const confetti = document.createElement("span");
  confetti.className = "ks-cart-goal-free-gift-confetti";
  confetti.setAttribute("aria-hidden", "true");

  for (let index = 0; index < 18; index += 1) {
    const angle = ((index * 137.5 - 90) * Math.PI) / 180;
    const distance = 55 + (index % 4) * 14;
    const piece = document.createElement("span");

    piece.className = "ks-cart-goal-free-gift-confetti-piece";
    piece.style.setProperty(
      "--ks-cart-goal-confetti-x",
      `${Math.round(Math.cos(angle) * distance)}px`,
    );
    piece.style.setProperty(
      "--ks-cart-goal-confetti-y",
      `${Math.round(Math.sin(angle) * distance)}px`,
    );
    piece.style.setProperty(
      "--ks-cart-goal-confetti-rotation",
      `${180 + index * 47}deg`,
    );
    piece.style.setProperty(
      "--ks-cart-goal-confetti-delay",
      `${(index % 6) * 0.025}s`,
    );
    piece.style.setProperty(
      "--ks-cart-goal-confetti-hue",
      String((index * 47) % 360),
    );
    confetti.append(piece);
  }

  confetti.addEventListener("animationend", (event) => {
    if (event.target === confetti) confetti.remove();
  });

  claim.append(confetti);
}

/*
  Shows the gift claim only while its selected goal is unlocked and the gift
  product is not already present in the cart.
*/
/**
 * @param {HTMLElement} element
 * @param {number} subtotal
 * @param {CartGoal[]} goals
 * @param {CartState} cart
 */
function updateFreeGiftClaims(element, subtotal, goals, cart) {
  element.querySelectorAll("[data-ks-cart-goal-free-gift]").forEach((claim) => {
    if (!(claim instanceof HTMLElement)) return;

    const giftGoalIndex = Number(claim.dataset.ksFreeGiftGoal);
    const giftGoal = goals.find((goal) => goal.index === giftGoalIndex);
    const giftInCart =
      getFreeGiftInCart(claim, cart) ??
      (claim.dataset.ksFreeGiftInCart === "true");
    const giftUnlocked = Boolean(giftGoal && subtotal >= giftGoal.amount);
    const wasHidden = claim.hidden;

    claim.dataset.ksFreeGiftInCart = String(giftInCart);
    claim.hidden = !giftUnlocked || giftInCart;

    if (claim.hidden) {
      claim
        .querySelector(".ks-cart-goal-free-gift-confetti")
        ?.remove();
    } else if (wasHidden) {
      showFreeGiftConfetti(claim);
    }
  });
}

/*
  Updates each label below the icons with the remaining spend for that goal,
  or the achieved text once the goal is reached.
*/
/**
 * @param {HTMLElement} element
 * @param {number} subtotal
 * @param {CartGoal[]} goals
 * @param {string} currency
 */
function updateGoalLabels(element, subtotal, goals, currency) {
  const remainingText =
    element.dataset.ksLabelRemainingText || "Spend [amount]";
  const achievedText = element.dataset.ksLabelAchievedText || "";

  element
    .querySelectorAll("[data-ks-cart-goal-label-index]")
    .forEach((label) => {
      if (!(label instanceof HTMLElement)) return;

      const goal = goals.find(
        (item) => item.index === Number(label.dataset.ksCartGoalLabelIndex),
      );
      const status = label.querySelector("[data-ks-cart-goal-label-status]");
      if (!goal || !(status instanceof HTMLElement)) return;

      const isAchieved = subtotal >= goal.amount;
      const text = isAchieved
        ? achievedText
        : remainingText.replaceAll(
            "[amount]",
            formatCurrency(goal.amount - subtotal, currency),
          );

      status.textContent = text;
      status.hidden = text === "";
      label.classList.toggle("ks-is-achieved", isAchieved);
    });
}

/*
  Updates one existing cart-goal element without rebuilding its markup.
*/
/**
 * @param {HTMLElement} element
 * @param {CartState} cart
 */
function updateCartGoal(element, cart) {
  const subtotal = Number(cart.subtotal);
  const quantity = Number(cart.quantity);
  const currency = String(cart.currency || "").toUpperCase();
  const goals = getGoals(element, currency);

  if (!Number.isFinite(subtotal) || quantity <= 0 || goals.length === 0) {
    element.hidden = true;
    return;
  }

  updateFreeGiftClaims(element, subtotal, goals, cart);
  updateGoalLabels(element, subtotal, goals, currency);

  const nextGoal = goals.find((goal) => subtotal < goal.amount);
  const progress = getProgress(subtotal, goals);
  const progressElement = element.querySelector("[data-ks-cart-goal-progress]");
  const progressFill = element.querySelector(
    "[data-ks-cart-goal-progress-fill]",
  );
  const progressMessage = element.querySelector(
    "[data-ks-cart-goal-progress-message]",
  );
  const completeMessage = element.querySelector(
    "[data-ks-cart-goal-complete-message]",
  );

  if (progressElement instanceof HTMLElement) {
    progressElement.setAttribute("aria-valuenow", String(Math.round(progress)));
  }

  if (progressFill instanceof HTMLElement) {
    progressFill.style.width = `${progress}%`;
  }

  if (progressMessage instanceof HTMLElement) {
    progressMessage.hidden = !nextGoal;
  }

  if (completeMessage instanceof HTMLElement) {
    completeMessage.hidden = Boolean(nextGoal);
  }

  if (nextGoal) {
    const remaining = Math.max(0, nextGoal.amount - subtotal);

    element
      .querySelectorAll("[data-ks-cart-goal-amount]")
      .forEach((placeholder) => {
        placeholder.textContent = formatCurrency(remaining, currency);
      });

    element
      .querySelectorAll("[data-ks-cart-goal-reward]")
      .forEach((placeholder) => {
        placeholder.textContent = nextGoal.label;
      });
  }

  const activeMessage = nextGoal ? progressMessage : completeMessage;
  const message = element.querySelector("[data-ks-cart-goal-message]");

  if (message instanceof HTMLElement) {
    message.setAttribute("value", activeMessage?.textContent?.trim() || "");
  }

  element.querySelectorAll("[data-ks-cart-goal-index]").forEach((icon) => {
    if (!(icon instanceof HTMLElement)) return;

    const goal = goals.find(
      (item) => item.index === Number(icon.dataset.ksCartGoalIndex),
    );
    const isAchieved = Boolean(goal && subtotal >= goal.amount);
    const isCurrent = Boolean(goal && nextGoal?.index === goal.index);

    icon.classList.toggle("ks-is-achieved", isAchieved);
    icon.classList.toggle("ks-is-current", isCurrent);

    if (isCurrent) {
      icon.setAttribute("aria-current", "step");
    } else {
      icon.removeAttribute("aria-current");
    }
  });

  element.hidden = false;
}


/* Reconnect automatically when Prestige replaces the cart drawer markup. */
class KsCartGoal extends HTMLElement {
  connectedCallback() {
    this.ksController?.abort();
    this.ksController = new AbortController();
    const signal = this.ksController.signal;
    this.ksUpdate({
      items_subtotal_price: Number(this.dataset.ksCartSubtotal) * 100,
      item_count: Number(this.dataset.ksCartQuantity),
      currency: this.dataset.ksCartCurrency
    });
    document.addEventListener('cart:change', event => {
      this.ksRequest?.abort();
      this.ksUpdate(event.detail.cart);
    }, { signal });
    document.addEventListener('cart:refresh', () => this.ksRefresh(), { signal });
    this.addEventListener('cart:error', event => {
      const error = this.querySelector('[data-ks-gift-error]');
      if (error) { error.textContent = event.detail.error; error.hidden = false; }
    }, { signal });
  }

  disconnectedCallback() {
    this.ksController?.abort();
    this.ksRequest?.abort();
  }

  ksUpdate(cart) {
    if (!cart) return;
    updateCartGoal(this, {
      subtotal: Number(cart.items_subtotal_price) / 100,
      quantity: cart.item_count,
      currency: cart.currency,
      lines: cart.items
    });
  }

  async ksRefresh() {
    this.ksRequest?.abort();
    this.ksRequest = new AbortController();
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`, { signal: this.ksRequest.signal });
      if (!response.ok) throw new Error('Cart refresh failed');
      this.ksUpdate(await response.json());
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('[KS Cart Goal]', error);
    }
  }
}
if (!customElements.get('ks-cart-goal')) customElements.define('ks-cart-goal', KsCartGoal);
